import { useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { PennylaneAggregationLevelEnum } from '@e-socna/data-layer';

import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';

import type { AggregationLevelVatWarnings } from './utils/compute-aggregation-level-vat-warnings.util';

const AGGREGATION_LEVEL_LABELS: Readonly<
  Record<PennylaneAggregationLevelEnum, string>
> = {
  [PennylaneAggregationLevelEnum.ServiceUsage]: 'Par prestation',
  [PennylaneAggregationLevelEnum.Group]: 'Par groupe',
  [PennylaneAggregationLevelEnum.Global]: 'Global',
};

const AGGREGATION_LEVELS = [
  PennylaneAggregationLevelEnum.ServiceUsage,
  PennylaneAggregationLevelEnum.Group,
  PennylaneAggregationLevelEnum.Global,
] as const;

const SERVICE_USAGE_LEVEL_UNAVAILABLE_EXPLANATION =
  "Le niveau d'agrégation par prestation nécessite qu'au moins un groupe soit en mode automatique.";

interface AggregationLevelValues {
  readonly aggregationLevel?: PennylaneAggregationLevelEnum;
}

interface Props {
  readonly defaultAggregationLevel: PennylaneAggregationLevelEnum;
  readonly isServiceUsageLevelAvailable: boolean;
  readonly vatWarningsByLevel: AggregationLevelVatWarnings;
}

export const AggregateServiceUsagesFields = ({
  defaultAggregationLevel,
  isServiceUsageLevelAvailable,
  vatWarningsByLevel,
}: Readonly<Props>) => {
  const { values } = useFormikContext<AggregationLevelValues>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const hasChoice = values.aggregationLevel !== undefined;

  useEffect(() => {
    if (!hasChoice) {
      setFieldValueOnNextTick('aggregationLevel', defaultAggregationLevel);
    }
  }, [defaultAggregationLevel, hasChoice, setFieldValueOnNextTick]);

  const handleChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setFieldValueOnNextTick('aggregationLevel', event.target.value);
    },
    [setFieldValueOnNextTick],
  );

  const aggregationLevel = values.aggregationLevel ?? defaultAggregationLevel;

  return (
    <Grid size={12} sx={{ pt: 1, pb: 1 }}>
      <FormControl variant="standard">
        <FormLabel id="mission-accounting-info-state-aggregation-level">
          Agréger les prestations dans Pennylane :
        </FormLabel>
        <RadioGroup
          aria-labelledby="mission-accounting-info-state-aggregation-level"
          name="aggregationLevel"
          onChange={handleChange}
          value={aggregationLevel}
        >
          {AGGREGATION_LEVELS.map((level) => {
            const isDisabled =
              level === PennylaneAggregationLevelEnum.ServiceUsage &&
              !isServiceUsageLevelAvailable;

            return (
              <Box key={level} sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Radio disabled={isDisabled} />}
                  label={AGGREGATION_LEVEL_LABELS[level]}
                  value={level}
                />
                {isDisabled ? (
                  <FormHelperText>
                    {SERVICE_USAGE_LEVEL_UNAVAILABLE_EXPLANATION}
                  </FormHelperText>
                ) : null}
                {vatWarningsByLevel[level].map((warning) => (
                  <FormHelperText key={warning}>{warning}</FormHelperText>
                ))}
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>
    </Grid>
  );
};
